/**
 * EditModelAvailabilityModal
 *
 * Modal to change a model's availability between global and restricted,
 * and manage tenant access grants for restricted models.
 */

import { AppModal } from '@/components/ui/app-modal';
import { useState } from 'react';
import {
  Globe,
  Lock,
  Plus,
  Trash2,
  Loader2,
  X,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useI18n } from '@/i18n';
import {
  useModelAccess,
  useUpdateModelAvailability,
  useGrantModelAccess,
  useRevokeModelAccess,
} from '../hooks/useModelAvailability';
import type { PlatformModel, ModelAvailability } from '../types';

interface EditModelAvailabilityModalProps {
  model: PlatformModel;
  onClose: () => void;
}

export function EditModelAvailabilityModal({
  model,
  onClose,
}: EditModelAvailabilityModalProps) {
  const { t } = useI18n();
  const [selectedAvailability, setSelectedAvailability] = useState<ModelAvailability>(
    model.availability
  );
  const [newTenantId, setNewTenantId] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: accessGrants, isLoading: isLoadingAccess } = useModelAccess(model.id);
  const updateAvailability = useUpdateModelAvailability();
  const grantAccess = useGrantModelAccess();
  const revokeAccess = useRevokeModelAccess();

  const hasChanged = selectedAvailability !== model.availability;

  const handleSaveAvailability = async () => {
    setError(null);
    try {
      await updateAvailability.mutateAsync({
        modelId: model.id,
        availability: selectedAvailability,
      });
      setSuccess(t('admin.availability.successUpdate'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('admin.availability.errorUpdate');
      setError(message);
    }
  };

  const handleGrantAccess = async () => {
    if (!newTenantId.trim()) return;
    setError(null);
    try {
      await grantAccess.mutateAsync({
        modelId: model.id,
        data: {
          tenant_id: newTenantId.trim(),
          notes: newNotes.trim() || undefined,
        },
      });
      setNewTenantId('');
      setNewNotes('');
      setShowGrantForm(false);
      setSuccess(t('admin.availability.successGrant'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('admin.availability.errorGrant');
      setError(message);
    }
  };

  const handleRevokeAccess = async (tenantId: string) => {
    setError(null);
    try {
      await revokeAccess.mutateAsync({ modelId: model.id, tenantId });
      setSuccess(t('admin.availability.successRevoke'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('admin.availability.errorRevoke');
      setError(message);
    }
  };

  return (
    <AppModal
      onClose={onClose}
      ariaLabel={t('admin.availability.title')}
      testId="availability-modal"
    >
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.availability.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {model.display_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error/Success messages */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg text-sm text-green-700 dark:text-green-300">
              ✓ {success}
            </div>
          )}

          {/* Availability selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('admin.availability.scope')}
            </label>
            <div className="space-y-3">
              <button
                onClick={() => setSelectedAvailability('global')}
                className={cn(
                  'w-full p-4 rounded-lg border-2 text-left transition-all',
                  selectedAvailability === 'global'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                )}
              >
                <div className="flex items-center gap-3">
                  <Globe className={cn(
                    'w-5 h-5',
                    selectedAvailability === 'global'
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-400'
                  )} />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {t('admin.availability.global')}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('admin.availability.globalDesc')}
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setSelectedAvailability('restricted')}
                className={cn(
                  'w-full p-4 rounded-lg border-2 text-left transition-all',
                  selectedAvailability === 'restricted'
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                )}
              >
                <div className="flex items-center gap-3">
                  <Lock className={cn(
                    'w-5 h-5',
                    selectedAvailability === 'restricted'
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-gray-400'
                  )} />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {t('admin.availability.restricted')}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('admin.availability.restrictedDesc')}
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {hasChanged && (
              <div className="mt-3 flex justify-end">
                <Button
                  onClick={handleSaveAvailability}
                  disabled={updateAvailability.isPending}
                  size="sm"
                >
                  {updateAvailability.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {t('admin.availability.saveChanges')}
                </Button>
              </div>
            )}
          </div>

          {/* Tenant Access section — only visible for restricted models */}
          {(selectedAvailability === 'restricted' || model.availability === 'restricted') && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('admin.availability.accessGrants')}
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowGrantForm(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t('admin.availability.grantAccess')}
                </Button>
              </div>

              {/* Grant form */}
              {showGrantForm && (
                <div className="p-4 mb-3 border dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('admin.availability.tenantId')}
                    </label>
                    <input
                      type="text"
                      value={newTenantId}
                      onChange={(e) => setNewTenantId(e.target.value)}
                      placeholder={t('admin.availability.tenantIdPlaceholder')}
                      className="w-full px-3 py-2 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('admin.availability.notes')}
                    </label>
                    <input
                      type="text"
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      placeholder={t('admin.availability.notesPlaceholder')}
                      className="w-full px-3 py-2 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowGrantForm(false);
                        setNewTenantId('');
                        setNewNotes('');
                      }}
                    >
                      {t('admin.availability.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleGrantAccess}
                      disabled={!newTenantId.trim() || grantAccess.isPending}
                    >
                      {grantAccess.isPending && (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      )}
                      {t('admin.availability.grant')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Access list */}
              {isLoadingAccess ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : accessGrants && accessGrants.length > 0 ? (
                <div className="space-y-2">
                  {accessGrants.map((grant) => (
                    <div
                      key={grant.id}
                      className="flex items-center justify-between p-3 border dark:border-gray-700 rounded-lg"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-mono text-gray-900 dark:text-gray-100 truncate">
                          {grant.tenant_id}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">
                            {t('admin.availability.granted')}: {new Date(grant.granted_at).toLocaleDateString()}
                          </span>
                          {grant.expires_at && (
                            <Badge variant="outline" className="text-xs">
                              {t('admin.availability.expires')}: {new Date(grant.expires_at).toLocaleDateString()}
                            </Badge>
                          )}
                          {grant.notes && (
                            <span className="text-xs text-gray-400 truncate max-w-[150px]">
                              {grant.notes}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevokeAccess(grant.tenant_id)}
                        disabled={revokeAccess.isPending}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400 border dark:border-gray-700 rounded-lg">
                  {t('admin.availability.noGrants')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppModal>
  );
}
